import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Edit3, ShoppingBag, Zap, Check } from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { useRealtime } from '../lib/useRealtime';
import { WaiterOrderEditor } from '../components/WaiterOrderEditor';
import { OrderItemsList } from '../components/OrderItemsList';
import { sortTablesByNumber } from '../lib/tableSort';
import { hasPlanFeature } from '../lib/plans';
import { PlanUpgradeNotice } from '../components/PlanUpgradeNotice';
import {
    STATUS_LABEL, extraCount, hasExtra, hasPendingExtra, readyToServeCount, readyToServeIndexes,
} from '../lib/orderFlow';
import { ReadyToServeSection } from '../components/ReadyToServeSection';

export default function WaiterPage() {
    const { user, restaurant } = useAuth();
    const [orders, setOrders] = useState([]);
    const [tables, setTables] = useState([]);
    const [editorOpen, setEditorOpen] = useState(false);
    const [editingOrder, setEditingOrder] = useState(null);
    const [initialTableId, setInitialTableId] = useState('');

    const reload = useCallback(() => {
        Promise.all([
            api.get('/r/orders'),
            api.get('/r/tables'),
        ]).then(([ordersRes, tablesRes]) => {
            setOrders(ordersRes.data.items.filter(o => !['PAID', 'CANCELLED'].includes(o.status)));
            setTables(sortTablesByNumber(tablesRes.data.items || []));
        });
    }, []);

    useEffect(() => { reload(); }, [reload]);
    useRealtime(user?.restaurant_id, (msg) => { if (['new_order', 'order_updated'].includes(msg.type)) reload(); });

    const setStatus = async (id, status) => { await api.put(`/r/orders/${id}/status`, { status }); reload(); };

    const advanceExtra = async (id, action) => {
        try { await api.post(`/r/orders/${id}/extra`, { action }); reload(); }
        catch (e) { alert(formatApiError(e)); }
    };

    const serveReady = async (id) => {
        try { await api.post(`/r/orders/${id}/serve-item`, { group: 'all' }); reload(); }
        catch (e) { alert(formatApiError(e)); }
    };

    const openForTable = (table, order = null) => {
        setEditingOrder(order);
        setInitialTableId(table?.id || '');
        setEditorOpen(true);
    };

    const closeEditor = () => {
        setEditorOpen(false);
        setEditingOrder(null);
        setInitialTableId('');
    };

    if (!hasPlanFeature(restaurant, 'waiter_view')) {
        return (
            <div className="fade-in-up" data-testid="waiter-page">
                <div className="kicker">Vedere Ospătar</div>
                <h1 className="headline" style={{ fontSize: '2.4rem', margin: '0.3rem 0 1.4rem' }}>Comenzile meselor mele.</h1>
                <PlanUpgradeNotice featureLabel="Vederea de ospătar" />
            </div>
        );
    }

    return (
        <div className="fade-in-up" data-testid="waiter-page">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: '1rem', flexWrap: 'wrap' }}>
                <div>
                    <div className="kicker">Vedere Ospătar</div>
                    <h1 className="headline" style={{ fontSize: '2.4rem', margin: '0.3rem 0 0.4rem' }}>Comenzile meselor mele.</h1>
                    <p style={{ color: 'var(--text-muted)', margin: 0 }}>
                        {tables.length > 0
                            ? 'Adaugă produse din meniu, editează sau șterge ce a comandat clientul de pe QR.'
                            : 'Nu ai mese asignate încă. Cere managerului să te repartizeze din Mese & QR.'}
                    </p>
                </div>
            </div>

            {tables.length > 0 && (
                <div style={{ marginBottom: '1.4rem' }}>
                    <div className="kicker" style={{ marginBottom: 8 }}>Mesele mele</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem' }}>
                        {tables.map((t) => {
                            const tableOrder = orders.find((o) => o.table_id === t.id);
                            return (
                                <div key={t.id} className="panel" data-testid={`waiter-table-${t.id}`}>
                                    <div style={{ fontWeight: 600, marginBottom: 4 }}>Masa {t.number}</div>
                                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 8 }}>{t.zone}</div>
                                    {tableOrder ? (
                                        <>
                                            <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 8 }}>
                                                {tableOrder.items.length} produs(e) · {tableOrder.total.toFixed(2)} RON
                                            </div>
                                            <button
                                                type="button"
                                                className="btn btn-primary btn-sm"
                                                style={{ width: '100%' }}
                                                onClick={() => openForTable(t, tableOrder)}
                                                data-testid={`waiter-table-add-${t.id}`}
                                            >
                                                <ShoppingBag size={14} /> Adaugă / Editează
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            type="button"
                                            className="btn btn-secondary btn-sm"
                                            style={{ width: '100%' }}
                                            onClick={() => openForTable(t)}
                                            data-testid={`waiter-table-new-${t.id}`}
                                        >
                                            <Plus size={14} /> Comandă nouă
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="kicker" style={{ marginBottom: 8 }}>Comenzi active</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.9rem' }}>
                {orders.map(o => (
                    <div key={o.id} className="panel panel-hover" data-testid={`waiter-order-${o.id}`}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                            <div className="headline" style={{ fontSize: '1.8rem' }}>Masa {o.table_number}</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                {hasExtra(o, 'new') && <span className="badge badge-magenta"><Zap size={11} style={{ marginRight: 2, verticalAlign: '-1px' }} /> EXTRA ({extraCount(o, 'new')})</span>}
                                {(hasExtra(o, 'sent') || hasExtra(o, 'cooking')) && <span className="badge badge-cyan">extra la bucătărie</span>}
                                {hasExtra(o, 'ready') && <span className="badge badge-amber">extra gata</span>}
                                <span className={`status-chip status-${o.status}`}>{STATUS_LABEL[o.status] || o.status}</span>
                            </div>
                        </div>
                        <div style={{ marginBottom: '0.7rem' }}>
                            <OrderItemsList items={o.items} variant="compact" />
                        </div>
                        {o.notes && (
                            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.6rem' }}>
                                Notă: {o.notes}
                            </div>
                        )}
                        <ReadyToServeSection order={o} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <span className="kicker">Total</span>
                            <strong style={{ color: 'var(--amber)' }}>{o.total.toFixed(2)} RON</strong>
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={() => openForTable(tables.find(t => t.id === o.table_id) || { id: o.table_id, number: o.table_number }, o)}
                                data-testid={`waiter-edit-${o.id}`}
                            >
                                <Edit3 size={14} /> Adaugă / Editează produse
                            </button>
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {hasExtra(o, 'new') && <button className="btn btn-primary btn-sm" style={{ flex: 1 }} onClick={() => advanceExtra(o.id, 'confirm')} data-testid={`waiter-extra-confirm-${o.id}`}><Zap size={13} style={{ marginRight: 3 }} /> Confirmă extra</button>}
                            {o.status === 'PENDING' && <button className="btn btn-primary btn-sm" style={{ flex: 1 }} onClick={() => setStatus(o.id, 'CONFIRMED')} data-testid={`waiter-confirm-${o.id}`}>Confirmă comanda</button>}
                            {/* ONE serve button: serves everything ready (bar + kitchen + ready extras). Auto-bumps to SERVED if it was the last. */}
                            {(() => {
                                const readyN = readyToServeCount(o, 'all') + extraCount(o, 'ready');
                                if (readyN > 0) {
                                    return (
                                        <button className="btn btn-primary btn-sm" style={{ flex: 1, background: 'var(--neon-teal)' }}
                                                onClick={() => serveReady(o.id)}
                                                data-testid={`waiter-serve-${o.id}`}>
                                            <Check size={13} style={{ marginRight: 3 }} /> Servește ({readyN})
                                        </button>
                                    );
                                }
                                // Legacy fallback: status READY but no item is per-item-ready (corrupt data).
                                if (o.status === 'READY' && readyToServeIndexes(o, 'all').length === 0) {
                                    return (
                                        <button className="btn btn-primary btn-sm" style={{ flex: 1, background: 'var(--neon-teal)' }}
                                                onClick={() => setStatus(o.id, 'SERVED')}
                                                data-testid={`waiter-serve-all-${o.id}`}>
                                            <Check size={13} style={{ marginRight: 3 }} /> Servește toate
                                        </button>
                                    );
                                }
                                return null;
                            })()}
                            {o.status === 'SERVED' && !hasPendingExtra(o) && <button className="btn btn-primary btn-sm" style={{ flex: 1 }} onClick={() => setStatus(o.id, 'PAID')} data-testid={`waiter-paid-${o.id}`}>Marchează plătită</button>}
                        </div>
                    </div>
                ))}
                {!orders.length && <div className="panel" style={{ gridColumn: '1/-1', textAlign: 'center', color: 'var(--text-muted)' }}>Nicio comandă activă acum.</div>}
            </div>

            <WaiterOrderEditor
                open={editorOpen}
                order={editingOrder}
                tables={tables}
                activeOrders={orders}
                initialTableId={initialTableId}
                onClose={closeEditor}
                onSaved={reload}
            />
        </div>
    );
}
